import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  MEMBERSHIP_SOURCE_LABELS,
  MEMBERSHIP_TIER_META,
  type MembershipTierCode,
} from "../lib/membership-tier-labels";
import type {
  CustomerAdminMembership,
  CustomerAdminMembershipVisit,
} from "@/modules/customers/domain/types";
import { cn } from "@/shared/lib/utils";
import { Link } from "@tanstack/react-router";
import { CrownIcon, Settings2Icon } from "lucide-react";

type Props = {
  membership: CustomerAdminMembership;
  visits: CustomerAdminMembershipVisit[];
};

function formatVisitWindowLabel(days: number) {
  if (days <= 0) return "ຕະຫຼອດຊີວິດ";
  return `${days.toLocaleString("lo-LA")} ວັນລ່າສຸດ`;
}

export function CustomerMembershipSection({ membership, visits }: Props) {
  const tier = membership.tier as MembershipTierCode;
  const meta = MEMBERSHIP_TIER_META[tier];
  const progress = membership.upgradeProgress ?? {
    nextTier: tier === "GOLD" ? null : tier === "SILVER" ? "GOLD" : "SILVER",
    currentVisits: membership.visitCountInWindow ?? 0,
    requiredVisits: null,
    visitWindowDays: 0,
  };
  const perks = membership.perks ?? {
    monthlyFreeCups: 0,
    discountPercent: 0,
    launchFree: false,
  };
  const progressPct =
    progress.requiredVisits != null && progress.requiredVisits > 0
      ? Math.min(
          100,
          Math.round(
            (progress.currentVisits / progress.requiredVisits) * 100,
          ),
        )
      : 100;

  const nextMeta =
    progress.nextTier != null
      ? MEMBERSHIP_TIER_META[progress.nextTier]
      : null;

  return (
    <div className="space-y-4">
      <div
        className={cn(
          "rounded-2xl border p-4 sm:p-5",
          meta.cardClass,
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span
              className={cn(
                "flex size-11 shrink-0 items-center justify-center rounded-xl border text-lg",
                meta.badgeClass,
              )}
            >
              {tier === "GOLD" ? "🥇" : tier === "SILVER" ? "🥈" : "👤"}
            </span>
            <div>
              <p className="text-muted-foreground text-xs font-medium tracking-wide">
                ລະດັບບັດສະມາຊິກ
              </p>
              <p className="font-bold text-xl tracking-tight">{meta.title}</p>
              <p className="text-muted-foreground mt-1 max-w-md text-xs leading-relaxed">
                {meta.description}
              </p>
            </div>
          </div>
          <Badge
            variant="outline"
            className={cn("shrink-0 font-semibold", meta.badgeClass)}
          >
            {meta.label}
          </Badge>
        </div>

        <dl className="mt-4 grid gap-2 sm:grid-cols-3">
          <div className="rounded-xl border border-border/60 bg-background/60 px-3 py-2.5">
            <dt className="text-muted-foreground text-[0.65rem] uppercase tracking-wide">
              ແກ້ວຟຣີ / ຮອບ
            </dt>
            <dd className="font-bold text-lg tabular-nums">
              {perks.monthlyFreeCups > 0
                ? perks.monthlyFreeCups.toLocaleString("lo-LA")
                : "—"}
            </dd>
          </div>
          <div className="rounded-xl border border-border/60 bg-background/60 px-3 py-2.5">
            <dt className="text-muted-foreground text-[0.65rem] uppercase tracking-wide">
              ສ່ວນຫຼຸດ tier
            </dt>
            <dd className="font-bold text-lg tabular-nums">
              {perks.discountPercent > 0 ? `${perks.discountPercent}%` : "—"}
            </dd>
          </div>
          <div className="rounded-xl border border-border/60 bg-background/60 px-3 py-2.5">
            <dt className="text-muted-foreground text-[0.65rem] uppercase tracking-wide">
              ເມນູ launch
            </dt>
            <dd className="font-medium text-sm">
              {perks.launchFree ? "ມີສິດຊິມຟຣີ" : "—"}
            </dd>
          </div>
        </dl>
      </div>

      {progress.nextTier && nextMeta ? (
        <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium">
              ຄວາມຄືບໜ້າຂຶ້ນ{" "}
              <span className="text-primary">{nextMeta.label}</span>
            </p>
            <span className="text-muted-foreground text-xs tabular-nums">
              {progress.currentVisits.toLocaleString("lo-LA")} /{" "}
              {progress.requiredVisits?.toLocaleString("lo-LA")} visit
            </span>
          </div>
          <div className="bg-muted h-2 overflow-hidden rounded-full">
            <div
              className="bg-primary h-full rounded-full transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-muted-foreground mt-2 text-xs">
            ນັບ visit ໃນ{formatVisitWindowLabel(progress.visitWindowDays)} —
            ຕາມການຕັ້ງຄ່າ auto upgrade
          </p>
        </div>
      ) : (
        <p className="text-muted-foreground rounded-2xl border border-dashed px-4 py-3 text-xs">
          ລູກຄ້າຢູ່ລະດັບສູງສຸດ (ບັດຄຳ) ແລ້ວ
        </p>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        <div className="flex items-center justify-between rounded-2xl border border-border/70 bg-muted/25 px-4 py-3">
          <span className="text-muted-foreground text-xs">Visit (window)</span>
          <span className="font-bold tabular-nums">
            {(membership.visitCountInWindow ?? 0).toLocaleString("lo-LA")}
          </span>
        </div>
        <div className="flex items-center justify-between rounded-2xl border border-border/70 bg-muted/25 px-4 py-3">
          <span className="text-muted-foreground text-xs">Visit (ທັງໝົດ)</span>
          <span className="font-bold tabular-nums">
            {(membership.visitCountAllTime ?? membership.visitCountInWindow ?? 0).toLocaleString("lo-LA")}
          </span>
        </div>
        <div className="flex items-center justify-between rounded-2xl border border-border/70 bg-muted/25 px-4 py-3 sm:col-span-2">
          <span className="text-muted-foreground text-xs">ແຫຼ່ງ tier</span>
          <span className="text-sm font-medium">
            {MEMBERSHIP_SOURCE_LABELS[membership.source]}
          </span>
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="ghost" size="sm" className="gap-1.5" asChild>
          <Link to="/app/membership/settings">
            <Settings2Icon className="size-4" />
            ການຕັ້ງຄ່າ upgrade / quota
          </Link>
        </Button>
      </div>

      {visits.length > 0 ? (
        <div className="space-y-2">
          <p className="text-muted-foreground text-xs font-medium">
            ປະຫວັດ visit (scan ທີ່ POS)
          </p>
          <ul className="divide-border max-h-48 divide-y overflow-y-auto rounded-2xl border border-border/70">
            {visits.map((visit) => (
              <li
                key={visit.id}
                className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {visit.orderNo ?? visit.orderId}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {visit.branchName ?? "—"}
                  </p>
                </div>
                <time
                  className="text-muted-foreground shrink-0 text-xs tabular-nums"
                  dateTime={visit.visitedAt}
                >
                  {new Date(visit.visitedAt).toLocaleString("lo-LA")}
                </time>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-muted-foreground text-xs">ຍັງບໍ່ມີ visit history</p>
      )}
    </div>
  );
}

export function CustomerMembershipEmpty() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-muted-foreground/25 border-dashed bg-muted/15 px-6 py-10 text-center">
      <div className="flex size-14 items-center justify-center rounded-2xl bg-muted/60 ring-1 ring-border/60">
        <CrownIcon className="size-7 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="space-y-1">
        <p className="font-medium text-foreground text-sm">
          ຍັງບໍ່ມີຂໍ້ມູນບັດສະມາຊິກ
        </p>
        <p className="max-w-[260px] text-muted-foreground text-xs leading-relaxed">
          ລູກຄ້າຈະມີລະດັບ «ສະມາຊິກທົ່ວໄປ» ຫຼັງລົງທະບຽນ — ຂຶ້ນ Silver/Gold ເມື່ອ scan ຄົບຕາມນະໂຍບາຍ
        </p>
      </div>
    </div>
  );
}
